import React, { useState, useEffect } from 'react';
import { X, Bug, Zap, FileText, BookOpen, CheckSquare, Flag, Calendar, User, Clock, Upload, Image, File, Trash2, Plus, Edit3, BarChart3 } from 'lucide-react';
import { Issue, User as UserType, Project } from '../../types';
import { MultiAssigneeSelector } from '../Issues/MultiAssigneeSelector';
import { apiService } from '../../services/api';
import { toast } from '../UI/Toast';
import { compressImage } from '../../utils/file';

interface LocalSubTask {
  id: string;
  title: string;
  description: string;
  status: string;
  priority: string;
  assignee_id: string;
  start_date: string;
  end_date: string;
  progress: number;
  color: string;
  order_index: number;
}

const GANTT_COLORS = ['#3B82F6', '#8B5CF6', '#EC4899', '#F59E0B', '#10B981', '#06B6D4', '#EF4444', '#6366F1', '#14B8A6', '#F97316'];

interface CreateIssueModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (issueData: any) => void;
  users: UserType[];
  projects: Project[];
  preSelectedProjectId?: string;
  currentUser?: UserType;
}

export const CreateIssueModal: React.FC<CreateIssueModalProps> = ({
  isOpen, onClose, onSave, users, projects, preSelectedProjectId, currentUser
}) => {
  const [projectMembers, setProjectMembers] = useState<UserType[]>([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    projectId: preSelectedProjectId || projects[0]?.id || '',
    summary: '', description: '',
    type: 'task' as const, status: 'to-do' as const, priority: 'medium' as const,
    assignee: '', assignees: [] as string[],
    reporter: currentUser?.id || users[0]?.id || '',
    labels: [] as string[], components: [] as string[], fixVersions: [] as string[],
    storyPoints: undefined as number | undefined,
    dueDate: '', sprint: '', epic: '',
    estimationTime: undefined as number | undefined,
    startDate: '', endDate: ''
  });
  const [newLabel, setNewLabel] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [uploadingFiles, setUploadingFiles] = useState(false);

  // Subtask state
  const [localSubtasks, setLocalSubtasks] = useState<LocalSubTask[]>([]);
  const [showSubtaskForm, setShowSubtaskForm] = useState(false);
  const [editingSubtaskId, setEditingSubtaskId] = useState<string | null>(null);
  const [stForm, setStForm] = useState({
    title: '', description: '', status: 'to-do', priority: 'medium',
    assignee_id: '', start_date: '', end_date: '', progress: 0, color: GANTT_COLORS[0]
  });

  useEffect(() => { if (formData.projectId) loadProjectMembers(formData.projectId); }, [formData.projectId]);

  useEffect(() => {
    if (isOpen) {
      setIsCreating(false); setErrors({});
      setFormData({
        projectId: preSelectedProjectId || projects[0]?.id || '',
        summary: '', description: '', type: 'task', status: 'to-do', priority: 'medium',
        assignee: '', assignees: [], reporter: currentUser?.id || users[0]?.id || '',
        labels: [], components: [], fixVersions: [], storyPoints: undefined,
        dueDate: '', sprint: '', epic: '', estimationTime: undefined,
        startDate: '', endDate: ''
      });
      setNewLabel(''); setAttachments([]); setUploadingFiles(false);
      setLocalSubtasks([]); setShowSubtaskForm(false); setEditingSubtaskId(null);
      setStForm({ title: '', description: '', status: 'to-do', priority: 'medium', assignee_id: '', start_date: '', end_date: '', progress: 0, color: GANTT_COLORS[0] });
    }
  }, [isOpen, preSelectedProjectId, projects, currentUser, users]);

  const loadProjectMembers = async (projectId: string) => {
    try { setIsLoadingMembers(true); const m = await apiService.getProjectMembers(projectId); setProjectMembers(m); }
    catch { setProjectMembers(users); } finally { setIsLoadingMembers(false); }
  };

  const validateForm = () => {
    const e: Record<string, string> = {};
    if (!formData.summary.trim()) e.summary = 'Summary is required';
    if (!formData.projectId) e.projectId = 'Project is required';
    if (!formData.reporter) e.reporter = 'Reporter is required';
    if (formData.reporter && !projectMembers.find(u => u.id === formData.reporter)) e.reporter = 'Invalid reporter';
    if (formData.startDate && formData.endDate && new Date(formData.startDate) > new Date(formData.endDate)) e.endDate = 'End date must be after start date';
    setErrors(e); return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault(); if (!validateForm()) return;
    try {
      setIsCreating(true); setErrors({}); toast.loading('Creating issue...', { id: 'create-issue' });
      const newIssue = await apiService.createIssue({
        project_id: formData.projectId, summary: formData.summary, description: formData.description,
        type: formData.type, status: formData.status, priority: formData.priority,
        assignees: formData.assignees, labels: formData.labels,
        story_points: formData.storyPoints, estimation_time: formData.estimationTime,
        due_date: formData.dueDate || undefined
      }, attachments);

      // Create subtasks
      if (localSubtasks.length > 0) {
        toast.loading('Creating sub-tasks...', { id: 'create-issue' });
        for (const st of localSubtasks) {
          try {
            const p: any = { title: st.title, description: st.description, status: st.status, priority: st.priority, progress: st.progress, color: st.color, order_index: st.order_index };
            if (st.assignee_id) p.assignee_id = st.assignee_id;
            if (st.start_date) p.start_date = st.start_date;
            if (st.end_date) p.end_date = st.end_date;
            await apiService.createSubTask(newIssue.id, p);
          } catch (err) { console.error('Failed to create subtask:', err); }
        }
      }

      const fi = {
        id: newIssue.id, key: newIssue.key, projectId: newIssue.project_id,
        summary: newIssue.summary, description: newIssue.description,
        type: newIssue.type, status: newIssue.status, priority: newIssue.priority,
        assignee: newIssue.assignee, assignees: newIssue.assignees,
        assignees_users: newIssue.assignees_users || [],
        reporter: newIssue.reporter, labels: newIssue.labels,
        components: formData.components, fixVersions: formData.fixVersions,
        storyPoints: newIssue.story_points, estimationTime: newIssue.estimation_time,
        createdAt: newIssue.created_at, updatedAt: newIssue.updated_at,
        dueDate: newIssue.due_date, subtasks: [], attachments: newIssue.attachments || [],
        comments: [], worklog: [], assignmentHistory: []
      };
      onSave(fi);

      let msg = localSubtasks.length > 0 ? `Issue created with ${localSubtasks.length} sub-task(s)!` : 'Issue created successfully!';
      // Email notifications are already sent by the backend when the issue is created,
      // so we don't need to call sendIssueNotification here (it would cause duplicate emails).
      onClose(); toast.success(msg, { id: 'create-issue' });
    } catch (error) {
      console.error('Failed to create issue:', error);
      setErrors({ submit: 'Failed to create issue. Please try again.' });
      toast.error('Failed to create issue.', { id: 'create-issue' });
    } finally { setIsCreating(false); }
  };

  const handleAssigneesChange = (ids: string[]) => setFormData({ ...formData, assignees: ids, assignee: ids[0] || '' });
  const addLabel = () => { if (newLabel.trim() && !formData.labels.includes(newLabel.trim())) { setFormData({ ...formData, labels: [...formData.labels, newLabel.trim()] }); setNewLabel(''); } };
  const removeLabel = (l: string) => setFormData({ ...formData, labels: formData.labels.filter(x => x !== l) });

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []); if (!files.length) return; setUploadingFiles(true);
    const allowed = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml', 'application/pdf', 'text/plain', 'text/csv', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation', 'application/zip', 'application/x-zip-compressed', 'application/json', 'application/xml'];
    const pf: File[] = [];
    for (const f of files) {
      if (!allowed.includes(f.type)) { setErrors(p => ({ ...p, attachments: `"${f.type}" not supported.` })); continue; }
      let proc = f; if (f.type.startsWith('image/')) proc = await compressImage(f);
      if (proc.size > 10 * 1024 * 1024) { setErrors(p => ({ ...p, attachments: `"${f.name}" too large.` })); continue; }
      pf.push(proc);
    }
    if (pf.length) { setAttachments(p => [...p, ...pf]); setErrors(p => { const { attachments: _, ...r } = p; return r; }); }
    setUploadingFiles(false); e.target.value = '';
  };

  const removeAttachment = (i: number) => setAttachments(p => p.filter((_, idx) => idx !== i));
  const fmtSize = (b: number) => { if (!b) return '0 B'; const k = 1024; const s = ['B', 'KB', 'MB', 'GB']; const i = Math.floor(Math.log(b) / Math.log(k)); return parseFloat((b / Math.pow(k, i)).toFixed(2)) + ' ' + s[i]; };
  const fileIcon = (f: File) => { if (f.type.startsWith('image/')) return <Image className="w-4 h-4 text-blue-600" />; if (f.type === 'application/pdf') return <File className="w-4 h-4 text-red-600" />; return <File className="w-4 h-4 text-gray-600" />; };
  const typeIcon = (t: string) => { switch (t) { case 'bug': return <Bug className="w-4 h-4 text-red-600" />; case 'story': return <Zap className="w-4 h-4 text-green-600" />; case 'task': return <CheckSquare className="w-4 h-4 text-blue-600" />; case 'epic': return <BookOpen className="w-4 h-4 text-purple-600" />; default: return <FileText className="w-4 h-4 text-gray-600" />; } };

  // Subtask helpers
  const resetSF = () => setStForm({ title: '', description: '', status: 'to-do', priority: 'medium', assignee_id: '', start_date: '', end_date: '', progress: 0, color: GANTT_COLORS[localSubtasks.length % GANTT_COLORS.length] });
  const addST = () => { if (!stForm.title.trim()) { toast.error('Title required'); return; } setLocalSubtasks(p => [...p, { id: `l-${Date.now()}`, ...stForm, order_index: p.length }]); setShowSubtaskForm(false); resetSF(); };
  const updateST = () => { if (!editingSubtaskId || !stForm.title.trim()) { toast.error('Title required'); return; } setLocalSubtasks(p => p.map(s => s.id === editingSubtaskId ? { ...s, ...stForm } : s)); setEditingSubtaskId(null); resetSF(); };
  const editST = (st: LocalSubTask) => { setEditingSubtaskId(st.id); setShowSubtaskForm(false); setStForm({ title: st.title, description: st.description, status: st.status, priority: st.priority, assignee_id: st.assignee_id, start_date: st.start_date, end_date: st.end_date, progress: st.progress, color: st.color }); };
  const currentProjectObj = projects.find(p => p.id === formData.projectId);
  const activeProjectMembers = (projectMembers || []).filter(user => {
    if (!user.active) return false;
    if (currentProjectObj && currentProjectObj.teamMembers) {
      return currentProjectObj.teamMembers.includes(user.id);
    }
    return true;
  });
  const membersList = [...(activeProjectMembers || []), ...(currentUser && !activeProjectMembers.some(m => m.id === currentUser.id) && currentUser.active ? [currentUser] : [])];
  const getAssName = (id: string) => membersList.find(u => u.id === id)?.displayName || 'Unassigned';

  const subtaskFormUI = (isEdit: boolean) => (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3 mt-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="md:col-span-2"><label className="block text-xs font-medium text-gray-600 mb-1">Title *</label>
          <input type="text" value={stForm.title} onChange={e => setStForm({ ...stForm, title: e.target.value })} className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500" placeholder="Sub-task title" /></div>
        <div className="md:col-span-2"><label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
          <textarea value={stForm.description} onChange={e => setStForm({ ...stForm, description: e.target.value })} rows={2} className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500" placeholder="Optional" /></div>
        <div><label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
          <select value={stForm.status} onChange={e => setStForm({ ...stForm, status: e.target.value })} className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md">
            <option value="to-do">To Do</option><option value="in-progress">In Progress</option><option value="done">Done</option></select></div>
        <div><label className="block text-xs font-medium text-gray-600 mb-1">Priority</label>
          <select value={stForm.priority} onChange={e => setStForm({ ...stForm, priority: e.target.value })} className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md">
            <option value="lowest">Lowest</option><option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="highest">Highest</option></select></div>
        <div><label className="block text-xs font-medium text-gray-600 mb-1">Assignee</label>
          <select value={stForm.assignee_id} onChange={e => setStForm({ ...stForm, assignee_id: e.target.value })} className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md">
            <option value="">Unassigned</option>{membersList.map(u => <option key={u.id} value={u.id}>{u.displayName}</option>)}</select></div>
        <div><label className="block text-xs font-medium text-gray-600 mb-1">Color</label>
          <div className="flex items-center space-x-1">{GANTT_COLORS.map(c => <button key={c} type="button" onClick={() => setStForm({ ...stForm, color: c })} className={`w-6 h-6 rounded-full border-2 ${stForm.color === c ? 'border-gray-800 scale-110' : 'border-transparent'}`} style={{ backgroundColor: c }} />)}</div></div>
        <div><label className="block text-xs font-medium text-gray-600 mb-1">Start Date</label>
          <input type="date" value={stForm.start_date} onChange={e => setStForm({ ...stForm, start_date: e.target.value })} className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md" /></div>
        <div><label className="block text-xs font-medium text-gray-600 mb-1">End Date</label>
          <input type="date" value={stForm.end_date} onChange={e => setStForm({ ...stForm, end_date: e.target.value })} className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md" /></div>
        <div className="md:col-span-2"><label className="block text-xs font-medium text-gray-600 mb-1">Progress: {stForm.progress}%</label>
          <input type="range" min="0" max="100" step="5" value={stForm.progress} onChange={e => setStForm({ ...stForm, progress: parseInt(e.target.value) })} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-green-600" /></div>
      </div>
      <div className="flex justify-end space-x-2 pt-2 border-t border-gray-200">
        <button type="button" onClick={() => { setShowSubtaskForm(false); setEditingSubtaskId(null); resetSF(); }} className="px-3 py-1.5 text-sm text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
        <button type="button" onClick={() => isEdit ? updateST() : addST()} className="px-3 py-1.5 text-sm text-white bg-green-600 rounded-md hover:bg-green-700">{isEdit ? 'Update' : 'Add Sub-task'}</button>
      </div>
    </div>
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-hidden">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-[90vh] flex overflow-hidden">
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-green-50 to-blue-50">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center"><CheckSquare className="w-6 h-6 text-white" /></div>
              <h2 className="text-lg font-semibold text-gray-900">Create New Issue</h2>
              {preSelectedProjectId && <span className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded">for {projects.find(p => p.id === preSelectedProjectId)?.name}</span>}
            </div>
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
          </div>

          <div className="flex-1 overflow-y-auto">
            <form id="create-issue-form" onSubmit={handleSubmit} className="p-6">
              {errors.submit && <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-400 rounded-lg"><p className="text-red-700 text-sm font-medium">⚠️ {errors.submit}</p></div>}

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                  {/* Project */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Project *</label>
                    <select required value={formData.projectId} onChange={e => setFormData({ ...formData, projectId: e.target.value })} className={`w-full px-3 py-2 text-xs border rounded-lg focus:ring-2 focus:ring-green-500 ${errors.projectId ? 'border-red-300' : 'border-gray-300'}`}>
                      <option value="">Select a project</option>
                      {projects.map(p => <option key={p.id} value={p.id}>{p.name} ({p.key})</option>)}
                    </select>
                    {errors.projectId && <p className="mt-1 text-xs text-red-600">{errors.projectId}</p>}
                  </div>
                  {/* Issue Type */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Issue Type *</label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {['story', 'task', 'bug', 'epic'].map(type => (
                        <label key={type} className={`flex items-center space-x-2 p-3 border rounded-lg cursor-pointer ${formData.type === type ? 'border-green-500 bg-green-50' : 'border-gray-300 hover:bg-gray-50'}`}>
                          <input type="radio" name="type" value={type} checked={formData.type === type} onChange={e => setFormData({ ...formData, type: e.target.value as any })} className="sr-only" />
                          {typeIcon(type)}<span className="text-xs font-medium capitalize">{type}</span>
                        </label>))}
                    </div>
                  </div>
                  {/* Summary */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Summary *</label>
                    <input type="text" required value={formData.summary} onChange={e => setFormData({ ...formData, summary: e.target.value })} className={`w-full px-3 py-2 text-xs border rounded-lg focus:ring-2 focus:ring-green-500 ${errors.summary ? 'border-red-300' : 'border-gray-300'}`} placeholder="Brief summary" />
                    {errors.summary && <p className="mt-1 text-xs text-red-600">{errors.summary}</p>}
                  </div>
                  {/* Description */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Description</label>
                    <textarea value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} rows={6} className="w-full px-3 py-2 text-xs leading-relaxed border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" placeholder="Detailed description" />
                  </div>

                  {/* Timeline */}
                  <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-3">
                      <Calendar className="w-5 h-5 text-indigo-600" />
                      <h3 className="text-sm font-semibold text-gray-900">Timeline</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-1">Start Date</label>
                        <input type="date" value={formData.startDate} onChange={e => setFormData({ ...formData, startDate: e.target.value })} className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-1">End Date</label>
                        <input type="date" value={formData.endDate} onChange={e => setFormData({ ...formData, endDate: e.target.value })} className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" />
                        {errors.endDate && <p className="mt-1 text-xs text-red-600">{errors.endDate}</p>}
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">Set a timeline for this issue. Sub-tasks can have their own timelines.</p>
                  </div>

                  {/* Sub-tasks */}
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <BarChart3 className="w-5 h-5 text-green-600" />
                        <h3 className="text-sm font-semibold text-gray-900">Sub-tasks</h3>
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{localSubtasks.length}</span>
                      </div>
                      <button type="button" onClick={() => { resetSF(); setEditingSubtaskId(null); setShowSubtaskForm(true); }} className="flex items-center space-x-1 px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700">
                        <Plus className="w-4 h-4" /><span>Add Sub-task</span>
                      </button>
                    </div>
                    {showSubtaskForm && subtaskFormUI(false)}
                    {editingSubtaskId && subtaskFormUI(true)}
                    {localSubtasks.length === 0 && !showSubtaskForm && (
                      <div className="text-center py-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                        <Clock className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                        <p className="text-sm text-gray-500">No sub-tasks yet</p>
                        <p className="text-xs text-gray-400">Add sub-tasks with timelines to track progress</p>
                      </div>
                    )}
                    {localSubtasks.length > 0 && (
                      <div className="space-y-2 mt-3">
                        {localSubtasks.map(st => (
                          <div key={st.id} className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg hover:shadow-sm group">
                            <div className="flex items-center space-x-3 flex-1 min-w-0">
                              <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: st.color }} />
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm font-medium truncate ${st.status === 'done' ? 'line-through text-gray-400' : 'text-gray-800'}`}>{st.title}</p>
                                <div className="flex items-center space-x-2 mt-0.5">
                                  {st.start_date && <span className="text-[10px] text-gray-400">{st.start_date}{st.end_date ? ` → ${st.end_date}` : ''}</span>}
                                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">{st.priority}</span>
                                  {st.assignee_id && <span className="text-[10px] text-gray-400">{getAssName(st.assignee_id)}</span>}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2 ml-2">
                              <div className="flex items-center space-x-1">
                                <div className="w-12 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                  <div className="h-full rounded-full" style={{ width: `${st.progress}%`, backgroundColor: st.color }} />
                                </div>
                                <span className="text-[10px] text-gray-500">{st.progress}%</span>
                              </div>
                              <button type="button" onClick={() => editST(st)} className="p-1 text-gray-400 hover:text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity"><Edit3 className="w-3.5 h-3.5" /></button>
                              <button type="button" onClick={() => delST(st.id)} className="p-1 text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="w-3.5 h-3.5" /></button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Attachments */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Attachments</label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-green-400 transition-colors">
                      <input type="file" multiple onChange={handleFileUpload} className="hidden" id="file-upload" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.json,.xml" />
                      <label htmlFor="file-upload" className="cursor-pointer">
                        <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-xs text-gray-600">Click to upload files</p>
                        <p className="text-xs text-gray-400 mt-1">Images, PDFs, documents up to 10MB</p>
                      </label>
                    </div>
                    {errors.attachments && <p className="mt-1 text-xs text-red-600">{errors.attachments}</p>}
                    {attachments.length > 0 && (
                      <div className="mt-3 space-y-2">
                        {attachments.map((file, index) => (
                          <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border border-gray-200">
                            <div className="flex items-center space-x-3">
                              {fileIcon(file)}
                              <div>
                                <p className="text-xs font-medium text-gray-900 truncate max-w-xs">{file.name}</p>
                                <p className="text-xs text-gray-500">{fmtSize(file.size)} • {file.type}</p>
                              </div>
                            </div>
                            <button type="button" onClick={() => removeAttachment(index)} className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors" title="Remove file">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Status</label>
                    <select value={formData.status} onChange={e => setFormData({ ...formData, status: e.target.value as any })} className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent">
                      <option value="backlog">Backlog</option>
                      <option value="to-do">To Do</option>
                      <option value="in-progress">In Progress</option>
                      <option value="in-review">In Review</option>
                      <option value="on-hold">On Hold</option>
                      <option value="done">Done</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Priority</label>
                    <select value={formData.priority} onChange={e => setFormData({ ...formData, priority: e.target.value as any })} className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent">
                      <option value="lowest">Lowest</option>
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="highest">Highest</option>
                    </select>
                  </div>

                  <MultiAssigneeSelector
                    users={activeProjectMembers}
                    selectedAssignees={formData.assignees}
                    onAssigneesChange={handleAssigneesChange}
                    assignmentHistory={[]}
                    currentUserId={currentUser?.id || ''}
                    issueKey="New Issue"
                    apiAssigneesUsers={[]}
                  />

                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Reporter *</label>
                    {currentUser ? (
                      <div className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <img src={currentUser.avatarUrl} alt={currentUser.displayName} className="w-6 h-6 rounded-full border border-gray-200" />
                          <span className="text-gray-900 font-medium">{currentUser.displayName}</span>
                        </div>
                      </div>
                    ) : (
                      <select required value={formData.reporter} onChange={e => setFormData({ ...formData, reporter: e.target.value })} className={`w-full px-3 py-2 text-xs border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${errors.reporter ? 'border-red-300' : 'border-gray-300'}`}>
                        <option value="">Select reporter</option>
                        {activeProjectMembers.map(user => <option key={user.id} value={user.id}>{user.displayName}</option>)}
                      </select>
                    )}
                    {errors.reporter && <p className="mt-1 text-xs text-red-600">{errors.reporter}</p>}
                    {isLoadingMembers && <p className="text-xs text-gray-500 mt-1">Loading project members...</p>}
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Story Points</label>
                    <input type="number" min="0" max="100" value={formData.storyPoints || ''} onChange={e => setFormData({ ...formData, storyPoints: e.target.value ? parseInt(e.target.value) : undefined })} className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent" placeholder="0" />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Estimation Time (Hours)</label>
                    <input type="number" min="0" step="0.5" value={formData.estimationTime || ''} onChange={e => setFormData({ ...formData, estimationTime: e.target.value ? parseFloat(e.target.value) : undefined })} className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent" placeholder="Enter hours (e.g., 8 or 8.5)" />
                    <p className="text-xs text-gray-500 mt-1">Enter estimated hours (supports decimals like 2.5 for 2 hours 30 minutes)</p>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Due Date</label>
                    <input type="date" value={formData.dueDate} onChange={e => setFormData({ ...formData, dueDate: e.target.value })} className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent" />
                  </div>

                  <div className="flex items-center justify-end space-x-4 mt-8 pt-6 border-t border-gray-200">
                    <button type="button" onClick={onClose} disabled={isCreating} className="px-4 py-2 text-xs text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50">Cancel</button>
                    <button type="submit" disabled={isCreating || uploadingFiles} className="px-6 py-2 text-xs bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2">
                      {isCreating ? (
                        <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div><span>Creating Issue...</span></>
                      ) : uploadingFiles ? (
                        <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div><span>Processing Files...</span></>
                      ) : (
                        <><CheckSquare className="w-4 h-4" /><span>Create Issue</span></>
                      )}
                    </button>
                  </div>
                </div>
              </div>

            </form>
          </div>
        </div>
      </div>
    </div>
  );
};